import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface ProductDetailDownPaymentModalProps {
  open: boolean;
  onClose: () => void;
  currentValue: number;
  maxValue: number;
  minValue: number;
  onSave: (value: number) => void;
  isPercentage?: boolean;
  productPrice?: number;
}

export const ProductDetailDownPaymentModal = ({
  open,
  onClose,
  currentValue,
  maxValue,
  minValue,
  onSave,
  isPercentage = false,
  productPrice = 500000,
}: ProductDetailDownPaymentModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [value, setValue] = useState(currentValue.toString());
  const [error, setError] = useState('');

  useEffect(() => {
    setValue(currentValue.toString());
    setError('');
  }, [currentValue, open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (error) setError('');

    const numValue = parseFloat(newValue);
    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }

    if (numValue < minValue) {
      setError(
        `Minimum value is ${
          isPercentage
            ? minValue.toFixed(1) + '%'
            : '$' +
              minValue.toLocaleString('en-US', { maximumFractionDigits: 0 })
        }`
      );
      return;
    }

    if (numValue > maxValue) {
      setError(
        `Maximum value is ${
          isPercentage
            ? maxValue.toFixed(1) + '%'
            : '$' +
              maxValue.toLocaleString('en-US', { maximumFractionDigits: 0 })
        }`
      );
      return;
    }
  };

  const handleSave = () => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < minValue || numValue > maxValue) {
      setError('Please enter a valid value within the allowed range');
      return;
    }

    onSave(numValue);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  const loanAmount = productPrice - parseFloat(value) || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          background: theme.palette.primary.main,
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          borderRadius: isMobile ? 0 : 3,
          boxShadow: theme.shadows[24],
          height: isMobile ? 'auto' : 'auto',
          maxHeight: isMobile ? '100vh' : 'auto',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.common.white,
            0.1
          )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          p: isMobile ? 2 : 3,
        }}
      >
        <Typography
          variant={isMobile ? 'h5' : 'h6'}
          fontWeight={700}
          color="white"
          sx={{ flex: 1 }}
        >
          Down Payment Configuration
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: isMobile ? 2 : 3, overflowY: 'auto' }}>
        {/* Down Payment Input Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700} color="white" gutterBottom>
            Enter Down Payment Amount
          </Typography>

          {/* Manual Input Field */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="white"
              gutterBottom
            >
              Down Payment Amount
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={value}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={`Enter amount between $${minValue.toLocaleString(
                'en-US',
                { maximumFractionDigits: 0 }
              )} - $${maxValue.toLocaleString('en-US', {
                maximumFractionDigits: 0,
              })}`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.common.white,
                    0.15
                  )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                  borderRadius: 2,
                  color: 'white',
                  '&:hover': {
                    borderColor: alpha(theme.palette.common.white, 0.5),
                  },
                  '&.Mui-focused': {
                    borderColor: 'white',
                    boxShadow: `0 0 0 2px ${alpha(
                      theme.palette.common.white,
                      0.2
                    )}`,
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  padding: isMobile ? 2 : 2.5,
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.6)',
                  opacity: 1,
                },
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    variant="body1"
                    color="white"
                    sx={{ mr: 1, fontWeight: 600 }}
                  >
                    $
                  </Typography>
                ),
              }}
            />
          </Box>

          {/* Summary Information */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              mb: 3,
            }}
          >
            {/* Down Payment */}
            <Box sx={{ flex: 1, width: '100%' }}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="white"
                gutterBottom
              >
                Down Payment
              </Typography>
              <Box
                sx={{
                  p: isMobile ? 1.5 : 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.common.white,
                    0.15
                  )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  fontWeight={800}
                  color="white"
                >
                  $
                  {parseFloat(value || '0').toLocaleString('en-US', {
                    maximumFractionDigits: 0,
                  })}
                </Typography>
                <Typography
                  variant="caption"
                  color="white"
                  sx={{ opacity: 0.8 }}
                >
                  {((parseFloat(value || '0') / productPrice) * 100).toFixed(1)}
                  % of purchase price
                </Typography>
              </Box>
            </Box>

            {/* Loan Amount */}
            <Box sx={{ flex: 1, width: '100%' }}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                color="white"
                gutterBottom
              >
                Loan Amount
              </Typography>
              <Box
                sx={{
                  p: isMobile ? 1.5 : 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.common.white,
                    0.15
                  )} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  fontWeight={800}
                  color="white"
                >
                  $
                  {loanAmount.toLocaleString('en-US', {
                    maximumFractionDigits: 0,
                  })}
                </Typography>
                <Typography
                  variant="caption"
                  color="white"
                  sx={{ opacity: 0.8 }}
                >
                  {((loanAmount / productPrice) * 100).toFixed(1)}% LTV
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.error.main,
                0.2
              )} 0%, ${alpha(theme.palette.error.main, 0.1)} 100%)`,
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              '& .MuiAlert-message': {
                color: 'white',
              },
            }}
          >
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: isMobile ? 2 : 3,
          background: theme.palette.primary.main,
          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth={isMobile}
          sx={{
            color: 'white',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          disabled={!!error || value === currentValue.toString()}
          fullWidth={isMobile}
          sx={{
            background: 'white',
            color: theme.palette.primary.main,
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.9)',
            },
            '&.Mui-disabled': {
              background: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
